import React from 'react';
import { ArrowTopRightOnSquareIcon, ArrowsPointingOutIcon, ClipboardDocumentListIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useSolicitacaoQuery } from '../../../hooks/data';
import { TIPO_SOLICITACAO_LABELS } from '../../NovaSolicitacaoPage/novaSolicitacaoPage.definitions';
import { STATUS_SOLICITACAO_BADGE_CLASS, STATUS_SOLICITACAO_LABELS } from '../solicitacoesListPage.definitions';

interface SolicitacoesMinimizadasProps {
  ids: string[];
  onReabrir: (id: string) => void;
  onFechar: (id: string) => void;
  onFecharTodas: () => void;
}

function CartaoMinimizada({ id, onReabrir, onFechar }: { id: string; onReabrir: () => void; onFechar: () => void }) {
  const { data: solicitacao } = useSolicitacaoQuery(id);
  return (
    <div className="w-[22rem] max-w-[calc(100vw-2.5rem)] rounded-xl border border-gray-200 bg-white p-4 shadow-xl">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
          <ClipboardDocumentListIcon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-montserrat font-semibold text-gray-900">
            {solicitacao ? TIPO_SOLICITACAO_LABELS[solicitacao.tipoSolicitacao] : 'Carregando...'}
          </p>
          {solicitacao && (
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
              <span className="truncate">{solicitacao.colaboradorNome}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_SOLICITACAO_BADGE_CLASS[solicitacao.status]}`}>
                {STATUS_SOLICITACAO_LABELS[solicitacao.status]}
              </span>
            </div>
          )}
        </div>
        <button
          type="button"
          className="-mr-1 -mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
          onClick={onFechar}
        >
          <span className="sr-only">Fechar</span>
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          className="btn-primary inline-flex flex-1 items-center justify-center gap-1.5 text-sm"
          onClick={onReabrir}
        >
          <ArrowsPointingOutIcon className="h-4 w-4" aria-hidden="true" />
          Reabrir
        </button>
        <a
          href={`/si/solicitacoes/${id}`}
          target="_blank"
          rel="noreferrer"
          className="btn-secondary inline-flex flex-1 items-center justify-center gap-1.5 text-sm"
          onClick={onFechar}
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          Nova aba
        </a>
      </div>
    </div>
  );
}

/**
 * Pilha de solicitações minimizadas no canto da lista — dá pra minimizar várias e abrir outras
 * enquanto elas esperam. A mais recente fica embaixo, mais perto da mão.
 */
export default function SolicitacoesMinimizadas({ ids, onReabrir, onFechar, onFecharTodas }: SolicitacoesMinimizadasProps) {
  if (ids.length === 0) return null;
  return (
    <div
      role="region"
      aria-label="Solicitações minimizadas"
      className="fixed bottom-5 right-5 z-40 flex max-h-[calc(100vh-2.5rem)] flex-col gap-3 overflow-y-auto"
    >
      {ids.length > 1 && (
        <button
          type="button"
          onClick={onFecharTodas}
          className="inline-flex items-center gap-1 self-end rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 shadow-md hover:bg-gray-50"
        >
          <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
          Fechar todas ({ids.length})
        </button>
      )}
      {ids.map((id) => (
        <CartaoMinimizada key={id} id={id} onReabrir={() => onReabrir(id)} onFechar={() => onFechar(id)} />
      ))}
    </div>
  );
}
